import { useGoals } from 'features/goals';
import { useHabits, useRepeatingTasks } from 'features/tasks';
import uniqBy from 'lodash/uniqBy';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Section, Sizes } from 'shared/components/ui/section';
import { TasksList } from 'shared/components/ui/tasks-list';
import { LifeArea } from 'shared/types/life-area';
import { Task } from 'shared/types/task';

export type ArchivedItemsListProps = {
  lifeArea: LifeArea;
};

export const ArchivedItemsList: React.FC<ArchivedItemsListProps> = ({
  lifeArea,
}) => {
  const { t } = useTranslation();

  const goals = useGoals({ lifeAreas: [lifeArea.id] });
  const goalIds = useMemo(() => goals.map((g) => g.id), [goals]);

  // Fetch items linked directly to the life area
  const directHabits = useHabits({
    lifeAreas: [lifeArea.id],
    archivedOnly: true,
  });
  const directRepeatingTasks = useRepeatingTasks({
    lifeAreas: [lifeArea.id],
    archivedOnly: true,
  });

  // Fetch items linked to the goals of the life area
  const goalHabits = useHabits({
    goals: goalIds,
    archivedOnly: true,
  });
  const goalRepeatingTasks = useRepeatingTasks({
    goals: goalIds,
    archivedOnly: true,
  });

  const archivedItems = useMemo(() => {
    const allItems = [...directHabits, ...directRepeatingTasks];

    if (goalIds.length) {
      allItems.push(...goalHabits, ...goalRepeatingTasks);
    }

    const uniqueItems = uniqBy(allItems, 'id');

    return uniqueItems.sort(
      (a, b) => (a.archivedAt?.getTime() ?? 0) - (b.archivedAt?.getTime() ?? 0),
    ) as Task[];
  }, [
    directHabits,
    directRepeatingTasks,
    goalHabits,
    goalRepeatingTasks,
    goalIds.length,
  ]);

  if (!archivedItems.length) {
    return null;
  }

  return (
    <Section
      title={t('pages.goal-detail.tasks.archived.title', 'Archived')}
      titleCount={archivedItems.length}
      size={Sizes.Small}
      canCollapse
    >
      <TasksList tasks={archivedItems} hideLifeArea />
    </Section>
  );
};
